import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Spinner, Form } from 'react-bootstrap';
import { AlertCircle, Check, CheckCircle, Clock, Search, User, X, XCircle } from 'lucide-react';
import ClaimedHoursService from '../../service/ClaimedHoursService';
import { minutosATexto } from '../../utils/hoursUtils';
import { useAuth } from '../../context/AuthContext';
import '../../styles/view/ApproveClaimedHours.css';

function getErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (data.message) return data.message;
  if (data.title) return data.title;
  if (data.errors) {
    const first = Object.values(data.errors)[0];
    return Array.isArray(first) ? first[0] : first;
  }
  return fallback;
}

export default function ApproveClaimedHours() {
  const { user } = useAuth();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [actionType, setActionType] = useState('approve');
  const [comment, setComment] = useState('');
  const [processing, setProcessing] = useState(false);

  const loadData = async () => {
    try {
      setError('');
      const data = await ClaimedHoursService.getPendingClaims();
      console.log(data);
      setClaims(data);
    } catch (err) {
      setError(getErrorMessage(err, 'No se pudo cargar la cola de reclamos pendientes.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredClaims = useMemo(() => {
    const text = search.trim().toLowerCase();
    if (!text) return claims;
    return claims.filter((claim) => {
      const nombre = `${claim.usuario?.nombre ?? ''} ${claim.usuario?.apellido ?? ''}`.toLowerCase();
      const cedula = `${claim.usuario?.cedula ?? ''}`.toLowerCase();
      return nombre.includes(text) || cedula.includes(text);
    });
  }, [claims, search]);

  const openAction = (claim, type) => {
    setSelected(claim);
    setActionType(type);
    setComment(type === 'approve' ? 'Aprobado' : '');
    setShowModal(true);
  };

  const handleAction = async () => {
    if (!selected) return;
    if (actionType === 'reject' && !comment.trim()) return;

    setProcessing(true);
    try {
      const payload = {
        aprobadorId: user?.id,
        comentarioOpcional: comment.trim() || undefined,
      };

      if (actionType === 'approve') {
        await ClaimedHoursService.approveClaim(selected.id, payload);
      } else {
        await ClaimedHoursService.rejectClaim(selected.id, payload);
      }

      setShowModal(false);
      setSelected(null);
      setComment('');
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'No se pudo procesar la decisión administrativa.'));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '45vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="ach-root container-fluid p-3 p-md-4">
      <div className="ach-header mb-4">
        <h3 className="mb-1">Aprobación de Retiros</h3>
        <p className="mb-0">Gestiona las solicitudes pendientes de HorasReclamadas.</p>
      </div>

      {error && (
        <div className="ach-alert mb-4">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button className="ach-alert-close" onClick={() => setError('')}>
            <X size={14} />
          </button>
        </div>
      )}

      <div className="ach-filters mb-3">
        <label className="ach-label">Buscar usuario</label>
        <div className="ach-search-wrap">
          <Search size={15} className="ach-search-icon" />
          <input
            className="form-control"
            type="text"
            value={search}
            placeholder="Nombre, apellido o cédula"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="ach-card">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Pendientes</h5>
          <span className="ach-badge-count">{filteredClaims.length}</span>
        </div>

        {filteredClaims.length === 0 ? (
          <div className="ach-empty">
            <CheckCircle size={44} />
            <p className="mb-0 mt-2">No hay reclamos pendientes por revisar.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Usuario</th>
                  <th>Solicitado</th>
                  <th>Fecha</th>
                  <th>Comentario</th>
                  <th className="text-end">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredClaims.map((claim) => (
                  <tr key={claim.id}>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <User size={15} />
                        <div>
                          <div className="fw-semibold">{claim.usuario?.nombre} {claim.usuario?.apellido}</div>
                          <small className="text-muted">{claim.usuario?.cedula || '—'}</small>
                        </div>
                      </div>
                    </td>
                    <td className="fw-semibold">{minutosATexto(claim.horasSolicitadas)}</td>
                    <td>{claim.fechaSolicitud ? new Date(claim.fechaSolicitud).toLocaleString('es-ES') : '—'}</td>
                    <td className="small text-muted">{claim.comentario || <span className="opacity-50">—</span>}</td>
                    <td className="text-end">
                      <div className="d-flex justify-content-end gap-2">
                        <button className="ach-btn approve" onClick={() => openAction(claim, 'approve')}>
                          <Check size={13} />
                          Aprobar
                        </button>
                        <button className="ach-btn reject" onClick={() => openAction(claim, 'reject')}>
                          <X size={13} />
                          Rechazar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal show={showModal} onHide={() => !processing && setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            {actionType === 'approve' ? <CheckCircle size={16} /> : <XCircle size={16} />}
            {actionType === 'approve' ? 'Aprobar retiro' : 'Rechazar retiro'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ach-modal-resume mb-3">
            <div><strong>Usuario:</strong> {selected?.usuario?.nombre} {selected?.usuario?.apellido}</div>
            <div><strong>Horas:</strong> {minutosATexto(selected?.horasSolicitadas ?? 0)}</div>
            <div><strong>Estado:</strong> <Clock size={13} className="me-1" />Pendiente</div>
          </div>

          <Form.Group>
            <Form.Label>
              {actionType === 'reject' ? 'Comentario de rechazo *' : 'Comentario (opcional)'}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={processing}
              placeholder={actionType === 'reject' ? 'Escribe el motivo del rechazo' : 'Comentario opcional'}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-outline-secondary" onClick={() => setShowModal(false)} disabled={processing}>
            Cancelar
          </button>
          <button
            className={`btn ${actionType === 'approve' ? 'btn-success' : 'btn-danger'}`}
            onClick={handleAction}
            disabled={processing || (actionType === 'reject' && !comment.trim())}
          >
            {processing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Procesando...
              </>
            ) : actionType === 'approve' ? (
              'Confirmar aprobación'
            ) : (
              'Confirmar rechazo'
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
